import { client } from "./api";

const SERVICE_WORKER_UPDATE_CHECK_MS = 60 * 1000;
const WEB_PUSH_SUBSCRIPTION_REFRESH_MS = 6 * 60 * 60 * 1000;
const WEB_PUSH_SUBSCRIPTION_REFRESH_KEY = "yara-web-push-subscription-refreshed-at";
const SKIP_WAITING_MESSAGE = { type: "YARA_SKIP_WAITING" } as const;
export const PRIVATE_API_CACHE_PREFIX = "yara-private-api-v1-";
const LEGACY_PRIVATE_API_CACHE_PATTERN = /^yara-web-.*-api$/;

let serviceWorkerRegistrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;
let updateHandlingStarted = false;
let reloadOnNextControllerChange = false;
let isReloadingForServiceWorkerUpdate = false;

export function isPrivateApiCacheName(cacheName: string) {
  return cacheName.startsWith(PRIVATE_API_CACHE_PREFIX) || LEGACY_PRIVATE_API_CACHE_PATTERN.test(cacheName);
}

export async function clearPrivateApiCaches(): Promise<void> {
  if (typeof caches === "undefined") return;
  const cacheNames = await caches.keys();
  await Promise.all(cacheNames.filter(isPrivateApiCacheName).map((cacheName) => caches.delete(cacheName)));
}

function activateWaitingWorker(worker: ServiceWorker | null | undefined) {
  worker?.postMessage(SKIP_WAITING_MESSAGE);
}

function startServiceWorkerUpdateHandling(registration: ServiceWorkerRegistration) {
  if (updateHandlingStarted || typeof window === "undefined" || typeof document === "undefined") return;
  updateHandlingStarted = true;
  reloadOnNextControllerChange = Boolean(navigator.serviceWorker.controller);

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!reloadOnNextControllerChange) {
      reloadOnNextControllerChange = true;
      return;
    }
    if (isReloadingForServiceWorkerUpdate) return;

    isReloadingForServiceWorkerUpdate = true;
    window.location.reload();
  });

  registration.addEventListener("updatefound", () => {
    const worker = registration.installing;
    if (!worker) return;

    worker.addEventListener("statechange", () => {
      if (worker.state === "installed" && navigator.serviceWorker.controller) {
        activateWaitingWorker(worker);
      }
    });
  });

  if (registration.waiting && navigator.serviceWorker.controller) {
    activateWaitingWorker(registration.waiting);
  }

  const checkForUpdate = () => {
    if (document.visibilityState === "hidden") return;
    registration.update().catch(console.warn);
  };

  window.setInterval(checkForUpdate, SERVICE_WORKER_UPDATE_CHECK_MS);
  window.addEventListener("online", checkForUpdate);
  window.addEventListener("focus", checkForUpdate);
  window.addEventListener("pageshow", checkForUpdate);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkForUpdate();
  });
  checkForUpdate();
}

export function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;

  serviceWorkerRegistrationPromise ??= navigator.serviceWorker
    .register("/sw.js", { updateViaCache: "none" })
    .then((registration) => {
      startServiceWorkerUpdateHandling(registration);
      return registration;
    })
    .catch((error) => {
      serviceWorkerRegistrationPromise = null;
      throw error;
    });

  return serviceWorkerRegistrationPromise;
}

function equalUint8Arrays(left: Uint8Array, right: Uint8Array): boolean {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index++) {
    if (left[index] !== right[index]) return false;
  }
  return true;
}

function subscriptionUsesApplicationServerKey(subscription: PushSubscription, publicKeyBytes: Uint8Array): boolean {
  const existingKey = subscription.options.applicationServerKey;
  if (!existingKey) return false;
  return equalUint8Arrays(new Uint8Array(existingKey), publicKeyBytes);
}

export function shouldRenewWebPushSubscription(subscription: PushSubscription, publicKeyBytes: Uint8Array): boolean {
  if (!subscriptionUsesApplicationServerKey(subscription, publicKeyBytes)) return true;
  return false;
}

function getLastWebPushSubscriptionRefresh(): number {
  try {
    return Number(window.localStorage.getItem(WEB_PUSH_SUBSCRIPTION_REFRESH_KEY) || 0);
  } catch {
    return 0;
  }
}

function setLastWebPushSubscriptionRefresh(value: number) {
  try {
    window.localStorage.setItem(WEB_PUSH_SUBSCRIPTION_REFRESH_KEY, String(value));
  } catch {
    // Storage can be unavailable; refreshing the subscription still works.
  }
}

export type WebPushSubscriptionIntent = {
  enableNotifications?: boolean;
};

export function createWebPushSubscriptionRequest(
  subscription: PushSubscriptionJSON,
  enableNotifications: boolean,
) {
  return {
    ...subscription,
    enableNotifications,
  };
}

export async function subscribeToWebPush({
  enableNotifications = true,
}: WebPushSubscriptionIntent = {}): Promise<boolean> {
  if (typeof window === "undefined" || !("Notification" in window) || !("PushManager" in window)) {
    throw new Error("Browser notifications are not supported on this device");
  }

  const publicKeyPromise = client.get<{ publicKey: string }>("/push/vapid-public-key");
  const registrationPromise = registerServiceWorker();

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    publicKeyPromise.catch(() => undefined);
    registrationPromise.catch(() => undefined);
    return false;
  }

  const [{ publicKey }, registration] = await Promise.all([publicKeyPromise, registrationPromise]);
  if (!registration) {
    throw new Error("Service workers are not supported");
  }
  const readyRegistration = await navigator.serviceWorker.ready;
  const publicKeyBytes = urlBase64ToUint8Array(publicKey);

  const existing = await readyRegistration.pushManager.getSubscription();
  if (existing && shouldRenewWebPushSubscription(existing, publicKeyBytes)) {
    await client.delete("/push/web/subscribe", { endpoint: existing.endpoint }).catch(() => undefined);
    await existing.unsubscribe().catch(() => undefined);
  }

  const current = await readyRegistration.pushManager.getSubscription();
  const subscription = current || await readyRegistration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: publicKeyBytes,
  });

  await client.post(
    "/push/web/subscribe",
    createWebPushSubscriptionRequest(subscription.toJSON(), enableNotifications),
  );
  setLastWebPushSubscriptionRefresh(Date.now());
  return true;
}

export async function refreshWebPushSubscription(): Promise<boolean> {
  if (typeof window === "undefined" || !("Notification" in window) || Notification.permission !== "granted") {
    return false;
  }

  if (Date.now() - getLastWebPushSubscriptionRefresh() < WEB_PUSH_SUBSCRIPTION_REFRESH_MS) {
    return true;
  }

  return subscribeToWebPush({ enableNotifications: false });
}

export async function unsubscribeFromWebPush({ notifyServer = true }: { notifyServer?: boolean } = {}): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  const registration = registrations[0] || (navigator.serviceWorker.controller ? await navigator.serviceWorker.ready : null);
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;
  try {
    if (notifyServer) {
      await client.delete("/push/web/subscribe", { endpoint: subscription.endpoint });
    }
  } finally {
    await subscription.unsubscribe();
  }
}
